import React, { useState, useEffect, useRef } from "react";
import { Navbar, Nav, Container, Modal } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHouse } from "@fortawesome/free-solid-svg-icons";
import { faEnvelope } from "@fortawesome/free-regular-svg-icons";
import { Squash as Hamburger } from 'hamburger-react';
import BrokerContactComponent from "../BrokerContactComponent/BrokerContactComponent";
import UserWayAccessibilityMenu from "../UserWayAccessibilityMenu/index";
import { isMobileOrTablet } from "../../utils/index";
import "./index.css";
import { Position } from "@react-pdf-viewer/core";

const HeaderNav = ({ brokerData, brokerUrl }) => {
  const [navbar, setNavbar] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [navbarExpanded, setNavbarExpanded] = useState(false);
  const navRef = useRef(null);
  const prevScrollPos = useRef(0);

  const getAngelAiPartnerUrl = () => {
    if (!brokerUrl) return "";
    return `https://www.angelai.com/?key=partner-${brokerUrl}`;
  };

  const gifStyle = {
    position: "relative",
    left: "10px",
    width: "40px",
    height: "auto",
    zIndex: "3",
  };

  const collapseNavbarIfMobile = () => {
    const isMobileOrTabletDevice = isMobileOrTablet();
    if (isMobileOrTabletDevice && navbarExpanded) {
      setNavbarExpanded(false);
    }
  };

  const scrollToForm = () => {
    const formSection = document.getElementById('ContactMeForm');
    if (formSection) {
      const elementPosition = formSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset;
      const offsetValue = -80;
      window.scrollTo({
        top: offsetPosition + offsetValue,
        behavior: 'smooth'
      });
    }
    collapseNavbarIfMobile();
  };

  const handleNavLinkClick = () => {
    collapseNavbarIfMobile();
  };

  const handleModalShow = () => {
    setShowModal(true);
    collapseNavbarIfMobile();
  };

  useEffect(() => {
    const handleHeaderScroll = () => {
      const isMobileOrTabletDevice = isMobileOrTablet();
      const currentScrollPos = window.scrollY;
      const Navbar = navRef.current;

      setScrollPosition(currentScrollPos);

      if (currentScrollPos === 0) {
        Navbar.classList.remove("navbarTransition");
        setNavbar(false);
      } else {
        if (prevScrollPos.current > currentScrollPos) {
          setNavbar(true);
          Navbar.classList.remove("navbarTransition");
        } else if (prevScrollPos.current < currentScrollPos) {
          if (!isMobileOrTabletDevice) {
            Navbar.classList.remove("navbarTransition");
            setNavbar(true);
          } else {
            setNavbar(false);
            Navbar.classList.add("navbarTransition");
          }
        }
      }
      setNavbarExpanded(false);
      prevScrollPos.current = currentScrollPos;
    };

    window.addEventListener("scroll", handleHeaderScroll);
    return () => window.removeEventListener("scroll", handleHeaderScroll);
  }, []);

  const handleToggle = () => {
    if (navbarExpanded) {
      setNavbarExpanded(false);
    } else {
      setNavbarExpanded(true);
    }
  };

  const handleClickOutside = (event) => {
    if (navRef.current && !navRef.current.contains(event.target)) {
      setNavbarExpanded(false);
    }
  };

  useEffect(() => {
    window.addEventListener("click", handleClickOutside);
    return () => {
      window.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const getNavbarHeight = () => {
    if (window.innerWidth <= 992) return 60;
    const maxHeight = 90;
    const minHeight = 70;
    const scrollThreshold = 200;
    const scrollRatio = Math.min(scrollPosition / scrollThreshold, 1);
    return maxHeight - scrollRatio * (maxHeight - minHeight);
  };

  const getLogoHeight = () => {
    if (window.innerWidth <= 992) return 50;
    const navbarHeight = getNavbarHeight();
    return navbarHeight * 0.9;
  };

  const handleModalClose = () => setShowModal(false);

  const navbarStyle = {
    height: `${getNavbarHeight()}px`,
  };

  const logoStyle = {
    height: `${getLogoHeight()}px`,
    maxWidth: '220px',
    transition: 'height 0.3s ease-in-out',
  };

  return (
    <header id="brokerNavbar">
      <Navbar
        id="Navbar"
        collapseOnSelect
        ref={navRef}
        expand="lg"
        fixed="top"
        bg="dark"
        variant="dark"
        className={`${navbar ? "navbar active" : "navbar"}`}
        style={navbarStyle}
        expanded={navbarExpanded}
      >
        <Container>
          <Navbar.Brand
            as={Link}
            to={`/${brokerUrl}`}
            className="d-flex align-items-center"
            onClick={handleNavLinkClick}
          >
            <img
              src={brokerData?.companyLogoUrl}
              alt="Broker Company Logo"
              className="main_logo"
              style={logoStyle}
            />
          </Navbar.Brand>

          <div
            className="d-lg-none custom-hamburger-wrapper"
            role="button"
            aria-label={navbarExpanded ? 'Close menu' : 'Open menu'}
            tabIndex={0}
            onClick={handleToggle}
            onKeyDown={(e) => e.key === 'Enter' && handleToggle()}
          >
            <Hamburger
              toggled={navbarExpanded}
              toggle={handleToggle}
              size={32}
              color="#B78585"
              duration={0.4}
              distance="md"
              rounded
            />
          </div>

          <Navbar.Collapse id="responsive-navbar-nav">
            <Nav className="ms-auto">
              <Nav.Link
                as={Link}
                to={`/${brokerUrl}`}
                onClick={handleNavLinkClick}
              >
                <FontAwesomeIcon icon={faHouse} className="me-1" /> Home
              </Nav.Link>
              <p className="mb-0 mt-2 navlinks-sperator">|</p>
              <Nav.Link onClick={scrollToForm}>
                <FontAwesomeIcon icon={faEnvelope} className="me-1" /> Contact Me
              </Nav.Link>

              <Nav.Link
                href={getAngelAiPartnerUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="angelai-btn"
                onClick={handleNavLinkClick}
              >
                AngelAi.com
              </Nav.Link>

              <Nav.Link
                href={brokerData?.angelaiUrl || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="ask-angel-btn"
                onClick={handleNavLinkClick}
              >
                <span>
                  <img src="https://staging-cdn.swmc.com/angelai/angelai-new-design/search-bar-angel-gif.gif"
                    className="ask-angelai-ballerina"
                    alt="Ask AngelAi"
                  />
                </span>
                Ask Angel
              </Nav.Link>

              <Nav.Link onClick={handleModalShow} className="apply-now-header-btn">
                Apply Now
              </Nav.Link>
              
              {/* Moved mobile accessibility inside the collapse menu */}
              <div className="d-lg-none mobileaccesibilty">
                <UserWayAccessibilityMenu color="black" width={40} height={40} />
              </div>
            </Nav>
          </Navbar.Collapse>
          
          {/* Desktop accessibility remains outside */}
          <div className="d-none d-lg-block desktopaccesibility">
            <UserWayAccessibilityMenu color="black" width={40} height={40} />
          </div>
        </Container>
      </Navbar>

      {/* Apply Now Modal */}
      <Modal show={showModal} onHide={handleModalClose} centered size="lg" className="Apply-now-header-modal">
        <Modal.Header closeButton>
          <Modal.Title>Apply Now</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <BrokerContactComponent brokerData={brokerData} />
        </Modal.Body>
      </Modal>
    </header>
  );
};

export default HeaderNav;